import { useEffect, useState, useRef } from 'react';
import { Dialog, DialogContent } from '@mui/material';
import { z } from 'zod';
import { FaBox, FaCreditCard, FaAngleDown, FaBoxOpen, FaTruck, FaTimes, FaSave, FaPlus, FaTrashAlt } from 'react-icons/fa';
import { FaMapMarkerAlt, FaFileInvoice } from 'react-icons/fa';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { IoIosCloseCircle } from "react-icons/io";
import { zodResolver } from '@hookform/resolvers/zod';
import { useMyContext } from '../../../context/MyContext';
import { FaWarehouse } from "react-icons/fa6";
import '../../css/custom-scrollbar.css';
import { Base_Url, updateB2b, b2bWarehouseListAll } from '../../../config/config';
import { toast } from 'react-toastify';
import { FaEdit } from 'react-icons/fa';

const schema = z.object({
    order_id: z.string()
        .min(1, "Order ID is required")
        .regex(/^[A-Za-z0-9-_]+$/, "Order ID must contain only alphanumeric characters, hyphens, and underscores"),
    lrn: z.string().optional(),
    warehouse_id: z.union([z.string(), z.number()]).optional(),
    total_boxes: z.number()
        .min(1, "Total boxes must be at least 1")
        .int("Total boxes must be a whole number"),
    total_weight: z.number()
        .min(0.1, "Total weight must be greater than 0")
        .max(1000000, "Total weight cannot exceed 1,000,000 kg"),
    rov_insurance: z.preprocess(
        (val) => (val === '' || val === null || (typeof val === 'number' && Number.isNaN(val)) ? undefined : val),
        z.number().min(0, "ROV Insurance cannot be negative").optional()
    ),
    total_length: z.preprocess(
        (val) => (val === '' || val === null || (typeof val === 'number' && Number.isNaN(val)) ? undefined : val),
        z.number().optional()
    ),
    total_width: z.preprocess(
        (val) => (val === '' || val === null || (typeof val === 'number' && Number.isNaN(val)) ? undefined : val),
        z.number().optional()
    ),
    total_height: z.preprocess(
        (val) => (val === '' || val === null || (typeof val === 'number' && Number.isNaN(val)) ? undefined : val),
        z.number().optional()
    ),
    freight_mode: z.string()
        .min(1, "Freight mode is required")
        .regex(/^[A-Za-z\s]+$/, "Freight mode must only contain letters and spaces"),
    shipment_type: z.string().optional(),
    shipment_order_id: z.string().optional(),
    shipment_weight: z.preprocess((val) => (val === '' || val === null || (typeof val === 'number' && Number.isNaN(val)) ? undefined : Number(val)), z.number().optional()),
    shipment_description: z.string().optional(),
    shipment_box_count: z.preprocess((val) => (val === '' || val === null || (typeof val === 'number' && Number.isNaN(val)) ? undefined : Number(val)), z.number().optional()),
    insurence: z.string().optional(),
    pickup_consignee_name: z.string()
        .min(1, "Pickup consignee name is required")
        .min(2, "Name must be at least 2 characters")
        .max(100, "Name cannot exceed 100 characters"),
    pickup_phone: z.string()
        .min(10, "Phone number must be at least 10 digits")
        .max(10, "Phone number cannot exceed 10 digits")
        .regex(/^[\+]?[\d\s\-\(\)]+$/, "Phone number format is invalid"),
    pickup_address: z.string()
        .min(10, "Pickup address must be at least 10 characters")
        .max(200, "Address cannot exceed 200 characters"),
    pickup_city: z.string()
        .min(1, "Pickup city is required")
        .min(2, "City must be at least 2 characters")
        .max(50, "City cannot exceed 50 characters")
        .regex(/^[A-Za-z\s.'-]+$/, "City must only contain letters, spaces, periods, apostrophes, and hyphens"),
    pickup_state: z.string()
        .min(1, "Pickup state is required")
        .min(2, "State must be at least 2 characters")
        .max(50, "State cannot exceed 50 characters")
        .regex(/^[A-Za-z\s.'-]+$/, "State must only contain letters, spaces, periods, apostrophes, and hyphens"),
    pickup_zip: z.string()
        .min(1, "Pickup ZIP code is required")
        .regex(/^[A-Za-z0-9\s-]+$/, "ZIP code format is invalid")
        .min(3, "ZIP code must be at least 3 characters")
        .max(10, "ZIP code cannot exceed 10 characters"),
    dropoff_consignee_name: z.string()
        .min(1, "Dropoff consignee name is required")
        .min(2, "Name must be at least 2 characters")
        .max(100, "Name cannot exceed 100 characters"),
    dropoff_phone: z.string()
        .min(10, "Phone number must be at least 10 digits")
        .max(15, "Phone number cannot exceed 15 digits")
        .regex(/^[\+]?[\d\s\-\(\)]+$/, "Phone number format is invalid"),
    dropoff_address: z.string()
        .min(10, "Dropoff address must be at least 10 characters")
        .max(200, "Address cannot exceed 200 characters"),
    dropoff_email: z.string()
        .email("Invalid email address")
        .optional()
        .or(z.literal("")),
    dropoff_city: z.string()
        .min(1, "Dropoff city is required")
        .min(2, "City must be at least 2 characters")
        .max(50, "City cannot exceed 50 characters")
        .regex(/^[A-Za-z\s.'-]+$/, "City must only contain letters, spaces, periods, apostrophes, and hyphens"),
    dropoff_state: z.string()
        .min(1, "Dropoff state is required")
        .min(2, "State must be at least 2 characters")
        .max(50, "State cannot exceed 50 characters")
        .regex(/^[A-Za-z\s.'-]+$/, "State must only contain letters, spaces, periods, apostrophes, and hyphens"),
    dropoff_zip: z.string()
        .min(1, "Dropoff ZIP code is required")
        .regex(/^[A-Za-z0-9\s-]+$/, "ZIP code format is invalid")
        .min(3, "ZIP code must be at least 3 characters")
        .max(10, "ZIP code cannot exceed 10 characters"),
    billing_name: z.string()
        .min(1, "Billing name is required")
        .min(2, "Name must be at least 2 characters")
        .max(100, "Name cannot exceed 100 characters")
        .regex(/^[a-zA-Z\s.'-]+$/, "Name must only contain letters, spaces, periods, apostrophes, and hyphens"),
    billing_company: z.string()
        .min(1, "Company name is required")
        .min(2, "Company name must be at least 2 characters")
        .max(100, "Company name cannot exceed 100 characters"),
    billing_consignor: z.string()
        .min(1, "Consignor is required")
        .min(2, "Consignor must be at least 2 characters")
        .max(100, "Consignor cannot exceed 100 characters")
        .regex(/^[a-zA-Z\s.'-]+$/, "Consignor must only contain letters, spaces, periods, apostrophes, and hyphens"),
    billing_phone: z.string()
        .min(10, "Phone number must be at least 10 digits")
        .max(15, "Phone number cannot exceed 15 digits")
        .regex(/^[\+]?[\d\s\-\(\)]/, "Phone number format is invalid"),
    billing_address: z.string()
        .min(10, "Billing address must be at least 10 characters")
        .max(200, "Address cannot exceed 200 characters"),
    billing_city: z.string()
        .min(1, "Billing city is required")
        .min(2, "City must be at least 2 characters")
        .max(50, "City cannot exceed 50 characters")
        .regex(/^[A-Za-z\s.'-]+$/, "City must only contain letters, spaces, periods, apostrophes, and hyphens"),
    billing_state: z.string()
        .min(1, "Billing state is required")
        .min(2, "State must be at least 2 characters")
        .max(50, "State cannot exceed 50 characters")
        .regex(/^[A-Za-z\s.'-]+$/, "State must only contain letters, spaces, periods, apostrophes, and hyphens"),
    billing_pin: z.string()
        .min(1, "PIN code is required")
        .regex(/^[A-Za-z0-9\s-]+$/, "PIN code format is invalid")
        .min(3, "PIN code must be at least 3 characters")
        .max(10, "PIN code cannot exceed 10 characters"),
    billing_pan_number: z.string()
        .regex(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, "PAN number must be in format: ABCDE1234F")
        .optional()
        .or(z.literal("")),
    billing_gst_number: z.string()
        .regex(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/, "GST number format is invalid")
        .optional()
        .or(z.literal("")),
    payment_id: z.string().optional(),
    invoice_ewaybill: z.string().optional(),
    invoice_num: z.string().optional(),
    invoice_amt: z.union([z.string(), z.number()]).optional(),
    payment_mode: z.string().optional(),
    inv_amt: z.union([z.string(), z.number()]).optional(),
});

const SectionBadge = ({ icon, gradient }) => (
    <div className={`flex items-center justify-center w-7 h-7 rounded-lg shadow-md bg-gradient-to-br ${gradient} text-white flex-shrink-0`}>
        {icon}
    </div>
);

const B2BViewAllShipment = ({ isopen, onClose, shipment, fetchOrderData, wareHouses = [] }) => {
    const { isDarkMode } = useMyContext();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const token = localStorage.getItem('token');
    const cus_id = localStorage.getItem('cus_id');

    const [boxSets, setBoxSets] = useState([{ id: 1, box_count: '', length: '', width: '', height: '' }]);
    const boxSetIdRef = useRef(1);

    const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm({
        resolver: zodResolver(schema),
        defaultValues: {
            order_id: '',
            lrn: '',
            warehouse_id: '',
            payment_mode: '',
            payment_id: '',
            cod_amount: '',
            total_boxes: '',
            total_weight: '',
            total_length: '',
            total_width: '',
            total_height: '',
            rov_insurance: '',
            freight_mode: '',
            shipment_type: '',
            insurence: '',
            pickup_consignee_name: '',
            pickup_address: '',
            pickup_city: '',
            pickup_state: '',
            pickup_zip: '',
            pickup_phone: '',
            dropoff_consignee_name: '',
            dropoff_address: '',
            dropoff_city: '',
            dropoff_state: '',
            dropoff_zip: '',
            dropoff_phone: '',
            dropoff_email: '',
            billing_name: '',
            billing_company: '',
            billing_consignor: '',
            billing_address: '',
            billing_city: '',
            billing_state: '',
            billing_pin: '',
            billing_phone: '',
            billing_pan_number: '',
            billing_gst_number: '',
            invoice_ewaybill: '',
            invoice_num: '',
            invoice_amt: '',
            invoice_qr_code: '',
            shipment_order_id: '',
            shipment_box_count: '',
            shipment_description: '',
            shipment_weight: '',
            shipment_master: '',
        }
    });
    const paymentMode = watch('payment_mode');
    const selectedWarehouseId = watch('warehouse_id');
    const totalBoxesWatch = watch('total_boxes');

    const isEditable = (fieldName) => {
        const editableFields = [
            'lrn', 'warehouse_id', 'payment_mode', 'payment_id', 'cod_amount', 'total_boxes',
            'total_weight', 'total_length', 'total_width', 'total_height',
            'rov_insurance', 'freight_mode', 'shipment_type', 'insurence',
            'pickup_consignee_name', 'pickup_address', 'pickup_city', 'pickup_state',
            'pickup_zip', 'pickup_phone', 'dropoff_consignee_name', 'dropoff_address',
            'dropoff_city', 'dropoff_state', 'dropoff_zip', 'dropoff_phone',
            'dropoff_email', 'billing_name', 'billing_company', 'billing_consignor',
            'billing_address', 'billing_city', 'billing_state', 'billing_pin',
            'billing_phone', 'billing_pan_number', 'billing_gst_number',
            'invoice_ewaybill', 'invoice_num', 'invoice_amt'
        ];
        return editableFields.includes(fieldName) && isEditing;
    };

    useEffect(() => {
        if (shipment) {
            try {
                let pickup = {};
                let dropoff = {};
                let billing = {};
                let invoices = [];
                let shipmentDetails = [];

                try {
                    pickup = typeof shipment.pickup_location === 'object' && shipment.pickup_location !== null
                        ? shipment.pickup_location
                        : JSON.parse(shipment.pickup_location || '{}');
                } catch (e) { pickup = {}; }

                try {
                    dropoff = typeof shipment.dropoff_location === 'object' && shipment.dropoff_location !== null
                        ? shipment.dropoff_location
                        : JSON.parse(shipment.dropoff_location || '{}');
                } catch (e) { dropoff = {}; }

                try {
                    billing = typeof shipment.billing_address === 'object' && shipment.billing_address !== null
                        ? shipment.billing_address
                        : JSON.parse(shipment.billing_address || '{}');
                } catch (e) { billing = {}; }

                try {
                    invoices = typeof shipment.invoices === 'object' && shipment.invoices !== null
                        ? (Array.isArray(shipment.invoices) ? shipment.invoices : [shipment.invoices])
                        : JSON.parse(shipment.invoices || '[]');
                } catch (e) { invoices = []; }

                try {
                    shipmentDetails = typeof shipment.shipment_details === 'object' && shipment.shipment_details !== null
                        ? (Array.isArray(shipment.shipment_details) ? shipment.shipment_details : [shipment.shipment_details])
                        : JSON.parse(shipment.shipment_details || '[]');
                } catch (e) { shipmentDetails = []; }

                setValue('order_id', shipment.order_id || '');
                setValue('lrn', shipment.lrn || '');
                setValue('payment_mode', shipment.payment_mode || '');
                setValue('payment_id', shipment.payment_id || '');
                setValue('cod_amount', shipment.cod_amount || '');
                setValue('total_boxes', shipment.total_boxes || '');
                setValue('total_weight', shipment.total_weight || '');
                setValue('total_length', shipment.total_length || '');
                setValue('total_width', shipment.total_width || '');
                setValue('total_height', shipment.total_height || '');
                setValue('rov_insurance', shipment.rov_insurance || '');
                setValue('freight_mode', shipment.freight_mode || '');
                setValue('shipment_type', shipment.shipment_type || '');
                setValue('insurence', shipment.insurence || '');

                setValue('pickup_consignee_name', pickup.consignee_name || '');
                setValue('pickup_address', pickup.address || '');
                setValue('pickup_city', pickup.city || '');
                setValue('pickup_state', pickup.state || '');
                setValue('pickup_zip', pickup.zip || '');
                setValue('pickup_phone', pickup.phone || '');

                setValue('dropoff_consignee_name', dropoff.consignee_name || '');
                setValue('dropoff_address', dropoff.address || '');
                setValue('dropoff_city', dropoff.city || '');
                setValue('dropoff_state', dropoff.state || '');
                setValue('dropoff_zip', dropoff.zip || '');
                setValue('dropoff_phone', dropoff.phone || '');
                setValue('dropoff_email', dropoff.email || '');

                setValue('billing_name', billing.name || '');
                setValue('billing_company', billing.company || '');
                setValue('billing_consignor', billing.consignor || '');
                setValue('billing_address', billing.address || '');
                setValue('billing_city', billing.city || '');
                setValue('billing_state', billing.state || '');
                setValue('billing_pin', billing.pin || '');
                setValue('billing_phone', billing.phone || '');
                setValue('billing_pan_number', billing.pan_number || '');
                setValue('billing_gst_number', billing.gst_number || '');

                const firstInvoice = invoices.length > 0 ? invoices[0] : {};
                setValue('invoice_ewaybill', firstInvoice.ewaybill || firstInvoice.e_waybill || shipment.ewaybill || shipment.e_waybill || '');
                
                const resolvedInvNum = firstInvoice.inv_num || firstInvoice.invoice_num || firstInvoice.invoice_number || shipment.invoice_number || shipment.invoice_num || '';
                setValue('invoice_num', resolvedInvNum);

                const resolvedInvAmt = firstInvoice.inv_amt !== undefined && firstInvoice.inv_amt !== null && firstInvoice.inv_amt !== ''
                    ? firstInvoice.inv_amt
                    : (firstInvoice.invoice_amt !== undefined && firstInvoice.invoice_amt !== null && firstInvoice.invoice_amt !== ''
                        ? firstInvoice.invoice_amt
                        : (firstInvoice.invoice_amount !== undefined && firstInvoice.invoice_amount !== null && firstInvoice.invoice_amount !== ''
                            ? firstInvoice.invoice_amount
                            : (firstInvoice.amount !== undefined && firstInvoice.amount !== null && firstInvoice.amount !== ''
                                ? firstInvoice.amount
                                : (shipment.invoice_amount || shipment.inv_amt || shipment.debit_amount || shipment.amount || shipment.total_amount || ''))));

                setValue('invoice_amt', resolvedInvAmt !== '' && resolvedInvAmt !== undefined && resolvedInvAmt !== null ? String(resolvedInvAmt) : '');

                if (shipmentDetails.length > 0) {
                    const firstShipment = shipmentDetails[0];
                    setValue('shipment_order_id', firstShipment.order_id || shipment.order_id || '');
                    setValue('shipment_box_count', firstShipment.box_count || shipment.total_boxes || '');
                    setValue('shipment_description', firstShipment.description || '');
                    setValue('shipment_weight', firstShipment.weight || shipment.total_weight || '');
                    setValue('shipment_master', firstShipment.master || '');
                } else {
                    setValue('shipment_order_id', shipment.order_id || '');
                    setValue('shipment_box_count', shipment.total_boxes || '');
                    setValue('shipment_description', shipment.product_desc || shipment.description || 'General Goods');
                    setValue('shipment_weight', shipment.total_weight || '');
                    setValue('shipment_master', 'False');
                }

                try {
                    const dims = shipment.dimensions ? JSON.parse(shipment.dimensions) : [];
                    if (Array.isArray(dims) && dims.length > 0) {
                        boxSetIdRef.current = dims.length;
                        setBoxSets(
                            dims.map((d, idx) => ({
                                id: idx + 1,
                                box_count: String(d.box_count ?? ''),
                                length: String(d.length ?? ''),
                                width: String(d.width ?? ''),
                                height: String(d.height ?? ''),
                            }))
                        );
                    } else {
                        boxSetIdRef.current = 1;
                        setBoxSets([{
                            id: 1,
                            box_count: String(shipment.total_boxes || ''),
                            length: String(shipment.total_length || ''),
                            width: String(shipment.total_width || ''),
                            height: String(shipment.total_height || ''),
                        }]);
                    }
                } catch (e) {
                    boxSetIdRef.current = 1;
                    setBoxSets([{ id: 1, box_count: String(shipment.total_boxes || ''), length: '', width: '', height: '' }]);
                }

            } catch (error) {
                console.error('Error parsing shipment data:', error);
                setError('Error parsing shipment data');
            }
        }
    }, [shipment, setValue]);

    useEffect(() => {
        if (!shipment) return;
        if (shipment.warehouse_id) {
            setValue('warehouse_id', shipment.warehouse_id);
            return;
        }
        if (!wareHouses || wareHouses.length === 0) return;
        try {
            const pickup = shipment.pickup_location ? JSON.parse(shipment.pickup_location) : {};
            const consigneeName = pickup?.consignee_name?.trim()?.toLowerCase();
            if (!consigneeName) return;
            const matched = wareHouses.find(
                (wh) => wh?.name?.trim()?.toLowerCase() === consigneeName
            );
            if (matched) {
                setValue('warehouse_id', matched.warehouse_id ?? matched.id ?? '');
            }
        } catch (e) {
            console.error('Error matching warehouse', e);
        }
    }, [shipment, wareHouses, setValue]);

    useEffect(() => {
        setBoxSets((prev) => {
            if (prev.length === 1) {
                return [{ ...prev[0], box_count: totalBoxesWatch === '' || totalBoxesWatch == null ? '' : String(totalBoxesWatch) }];
            }
            return prev;
        });
    }, [totalBoxesWatch]);

    const boxAssignedCount = boxSets.reduce((sum, s) => sum + (parseInt(s.box_count, 10) || 0), 0);
    const boxTotalNum = parseInt(totalBoxesWatch, 10) || 0;
    const boxRemaining = boxTotalNum - boxAssignedCount;

    const updateBoxSet = (id, field, value) => {
        setBoxSets((prev) => prev.map((s) => (s.id === id ? { ...s, [field]: value } : s)));
    };

    const addBoxSet = () => {
        if (!boxTotalNum) {
            toast.error('Enter Total Boxes ');
            return;
        }
        if (boxRemaining <= 0) {
            toast.error('All Boxes has already assigned.');
            return;
        }
        boxSetIdRef.current += 1;
        setBoxSets((prev) => [...prev, { id: boxSetIdRef.current, box_count: String(boxRemaining), length: '', width: '', height: '' }]);
    };

    const removeBoxSet = (id) => {
        setBoxSets((prev) => (prev.length > 1 ? prev.filter((s) => s.id !== id) : prev));
    };

    const onErrors = (errors) => {
        const firstKey = Object.keys(errors)[0];
        const firstError = firstKey ? errors[firstKey] : null;
        if (firstError?.message) {
            toast.error(`${firstKey}: ${firstError.message}`);
        } else {
            toast.error("Please fix the errors in the form.");
        }
        console.error("Form validation errors:", errors);
    };

    const onSubmit = async (data) => {
        for (const s of boxSets) {
            const qty = parseInt(s.box_count, 10) || 0;
            if (qty < 1 || !s.length || !s.width || !s.height) {
                toast.error('fill out all box set dimensions and ensure box count is at least 1.');
                return;
            }
        }
        if (boxAssignedCount !== boxTotalNum) {
            toast.error(
                boxAssignedCount < boxTotalNum
                    ? `${boxRemaining} boxes dimensions are missing.`
                    : ` ${Math.abs(boxRemaining)} `
            );
            return;
        }

        const url = Base_Url + updateB2b + "/" + data.order_id;
        try {
            setIsLoading(true);
            setError(null);

            const pickupLocation = {
                consignee_name: data.pickup_consignee_name,
                address: data.pickup_address,
                city: data.pickup_city,
                state: data.pickup_state,
                zip: data.pickup_zip,
                phone: data.pickup_phone,
            };

            const dropoffLocation = {
                consignee_name: data.dropoff_consignee_name,
                address: data.dropoff_address,
                city: data.dropoff_state,
                state: data.dropoff_state,
                zip: data.dropoff_zip,
                phone: data.dropoff_phone,
                email: data.dropoff_email
            };

            const billingAddress = {
                name: data.billing_name,
                company: data.billing_company,
                consignor: data.billing_consignor,
                address: data.billing_address,
                city: data.billing_city,
                state: data.billing_state,
                pin: data.billing_pin,
                phone: data.billing_phone,
                pan_number: data.billing_pan_number,
                gst_number: data.billing_gst_number
            };

            const invoices = [{
                ewaybill: data.invoice_ewaybill || '',
                inv_num: data.invoice_num || '',
                inv_amt: parseFloat(data.invoice_amt) || 0,
                inv_qr_code: ""
            }];
            const docData = [
                {
                    doc_type: "INVOICE_COPY",
                    doc_meta: {
                        invoice_num: [data.invoice_num || '']
                    }
                }
            ];

            let existingShipmentDetails = [];
            try {
                existingShipmentDetails = typeof shipment.shipment_details === 'object' && shipment.shipment_details !== null
                    ? (Array.isArray(shipment.shipment_details) ? shipment.shipment_details : [shipment.shipment_details])
                    : JSON.parse(shipment.shipment_details || '[]');
            } catch (e) { existingShipmentDetails = []; }
            const firstSD = existingShipmentDetails[0] || {};

            const shipmentDetails = [{
                order_id: data.shipment_order_id || firstSD.order_id || data.order_id || shipment.order_id || '',
                box_count: data.shipment_box_count || firstSD.box_count || data.total_boxes || 1,
                description: data.shipment_description || firstSD.description || 'General Goods',
                weight: data.shipment_weight || firstSD.weight || data.total_weight || 0,
                waybills: firstSD.waybills || [],
                master: data.shipment_master || firstSD.master || "False"
            }];

            const dimensions = boxSets.map((s) => ({
                box_count: parseInt(s.box_count, 10) || 0,
                length: parseFloat(s.length) || 0,
                width: parseFloat(s.width) || 0,
                height: parseFloat(s.height) || 0,
            }));

            if (!data.warehouse_id) {
                toast.error("Please select a warehouse before saving.");
                setIsLoading(false);
                return;
            }

            const payload = {
                order_id: data.order_id,
                warehouse_id: data.warehouse_id,
                retailer_id: shipment.user_id,
                lrn: data.lrn,
                payment_mode: data.payment_mode,
                cod_amount: data.payment_mode === "prepaid" ? 0 : data.cod_amount,
                payment_id: data.payment_id,
                total_boxes: data.total_boxes,
                total_weight: data.total_weight,
                weight: data.total_weight,
                total_length: dimensions[0]?.length ?? 0,
                total_width: dimensions[0]?.width ?? 0,
                total_height: dimensions[0]?.height ?? 0,
                rov_insurance: data.rov_insurance,
                freight_mode: data.freight_mode,
                shipment_type: data.shipment_type,
                insurence: data.insurence,
                dimensions: JSON.stringify(dimensions),
                pickup_location: JSON.stringify(pickupLocation),
                dropoff_location: JSON.stringify(dropoffLocation),
                billing_address: JSON.stringify(billingAddress),
                invoices: JSON.stringify(invoices),
                doc_file: shipment.doc_file_path,
                doc_data: JSON.stringify(docData),
                shipment_details: JSON.stringify(shipmentDetails)
            };

            if (data.payment_mode !== 'prepaid') {
                payload.cod_amount = data.cod_amount;
            }

            const response = await axios.post(url, payload, {
                headers: {
                    'Content-Type': 'application/json',
                    'token': token,
                    'cus_id': cus_id,
                },
            });

            if (response.data.status === "SUCCESS") {
                toast.success(response.data.message);
                setIsEditing(false);
                onClose();
                fetchOrderData && fetchOrderData();
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update shipment');
            toast.error(err.response?.data?.message || 'Failed to update shipment');
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = () => {
        setIsEditing(true);
    };

    const renderField = (label, fieldName, type = 'text', isNumeric = false, placeholder = '') => {
        const editable = isEditable(fieldName);
        let value = '';

        if (fieldName.startsWith('pickup_') && shipment?.pickup_location) {
            try {
                const pickup = JSON.parse(shipment.pickup_location);
                const key = fieldName.replace('pickup_', '');
                value = pickup[key] || '';
            } catch (e) {
                value = '';
            }
        } else if (fieldName.startsWith('dropoff_') && shipment?.dropoff_location) {
            try {
                const dropoff = JSON.parse(shipment.dropoff_location);
                const key = fieldName.replace('dropoff_', '');
                value = dropoff[key] || '';
            } catch (e) {
                value = '';
            }
        } else if (fieldName.startsWith('billing_') && shipment?.billing_address) {
            try {
                const billing = JSON.parse(shipment.billing_address);
                const key = fieldName.replace('billing_', '');
                value = billing[key] || '';
            } catch (e) {
                value = '';
            }
        } else if (fieldName.startsWith('invoice_')) {
            let invoices = [];
            try {
                invoices = typeof shipment?.invoices === 'object' && shipment?.invoices !== null
                    ? (Array.isArray(shipment.invoices) ? shipment.invoices : [shipment.invoices])
                    : JSON.parse(shipment?.invoices || '[]');
            } catch (e) {
                invoices = [];
            }
            const firstInvoice = invoices.length > 0 ? invoices[0] : {};
            const key = fieldName.replace('invoice_', '');
            if (key === 'amt') {
                const amtVal = firstInvoice.inv_amt !== undefined && firstInvoice.inv_amt !== null && firstInvoice.inv_amt !== ''
                    ? firstInvoice.inv_amt
                    : (firstInvoice.invoice_amt !== undefined && firstInvoice.invoice_amt !== null && firstInvoice.invoice_amt !== ''
                        ? firstInvoice.invoice_amt
                        : (firstInvoice.invoice_amount !== undefined && firstInvoice.invoice_amount !== null && firstInvoice.invoice_amount !== ''
                            ? firstInvoice.invoice_amount
                            : (firstInvoice.amount !== undefined && firstInvoice.amount !== null && firstInvoice.amount !== ''
                                ? firstInvoice.amount
                                : (shipment?.invoice_amount || shipment?.inv_amt || shipment?.debit_amount || shipment?.amount || shipment?.total_amount || ''))));
                value = amtVal !== '' && amtVal !== undefined && amtVal !== null ? amtVal : '';
            } else if (key === 'num') {
                value = firstInvoice.inv_num || firstInvoice.invoice_num || firstInvoice.invoice_number || shipment?.invoice_number || shipment?.invoice_num || '';
            } else if (key === 'ewaybill') {
                value = firstInvoice.ewaybill || firstInvoice.e_waybill || shipment?.ewaybill || shipment?.e_waybill || '';
            } else {
                value = firstInvoice[key] || '';
            }
        } else if (fieldName.startsWith('shipment_') && shipment?.shipment_details) {
            try {
                const shipmentDetails = JSON.parse(shipment.shipment_details);
                if (shipmentDetails.length > 0) {
                    const key = fieldName.replace('shipment_', '');
                    if (key === 'order_id') {
                        value = shipmentDetails[0].order_id || '';
                    } else if (key === 'box_count') {
                        value = shipmentDetails[0].box_count || '';
                    } else if (key === 'description') {
                        value = shipmentDetails[0].description || '';
                    } else if (key === 'weight') {
                        value = shipmentDetails[0].weight || '';
                    } else if (key === 'master') {
                        value = shipmentDetails[0].master || '';
                    }
                }
            } catch (e) {
                value = '';
            }
        } else {
            value = shipment?.[fieldName] || '';
            if (fieldName === 'cod_amount' && !value && shipment?.cod) {
                value = shipment.cod;
            }
        }

        return (
            <div>
                <label className={`block text-[10px] mb-1 font-semibold uppercase tracking-wide ${isDarkMode ? "text-indigo-200/80" : "text-gray-500"}`}>{label}</label>
                {editable ? (
                    <>
                        <input
                            type={type}
                            step={isNumeric ? "0.1" : undefined}
                            placeholder={placeholder}
                            className={`px-2.5 py-2 text-[13px] sm:text-sm rounded-lg border w-full outline-none transition-all duration-200 shadow-sm focus:shadow-md focus:ring-2 ${isDarkMode
                                ? "bg-[#1a1a2e] border-indigo-400/50 text-white placeholder-indigo-300/50 focus:border-indigo-400 focus:ring-indigo-500/40"
                                : "bg-white border-gray-200 text-gray-800 placeholder-gray-400 focus:border-indigo-400 focus:ring-indigo-500/15"
                                }`}
                            {...register(fieldName, isNumeric ? { valueAsNumber: true } : {})}
                        />
                        {errors[fieldName] && <p className="text-red-500 text-[10px] mt-0.5 font-medium">{errors[fieldName].message}</p>}
                    </>
                ) : (
                    <div className={`px-2.5 py-2 text-[13px] sm:text-sm rounded-lg border w-full truncate ${isDarkMode
                        ? "bg-indigo-500/[0.08] border-indigo-500/25 text-indigo-100"
                        : "bg-gray-50 border-gray-100 text-gray-700"
                        }`}>
                        <span className="font-medium">{value || <span className="italic text-gray-400 dark:text-indigo-300/50 font-normal">Not specified</span>}</span>
                    </div>
                )}
            </div>
        );
    };

    const renderWarehouseField = () => {
        const editable = isEditable('warehouse_id');
        const selectedWh = wareHouses.find(
            (wh) => String(wh.warehouse_id ?? wh.id) === String(selectedWarehouseId)
        );

        return (
            <div>
                <label className={`block text-[10px] mb-1 font-semibold uppercase tracking-wide ${isDarkMode ? "text-indigo-200/80" : "text-gray-500"}`}>
                    Warehouse
                </label>
                {editable ? (
                    <>
                        <div className="relative">
                            <select
                                {...register('warehouse_id')}
                                className={`px-2.5 py-2 text-[13px] sm:text-sm rounded-lg border w-full outline-none appearance-none transition-all shadow-sm focus:shadow-md focus:ring-2 ${isDarkMode
                                    ? "bg-[#1a1a2e] text-white border-indigo-400/50 focus:border-indigo-400 focus:ring-indigo-500/40"
                                    : "bg-white border-gray-200 focus:border-indigo-400 focus:ring-indigo-500/15"
                                    }`}
                            >
                                <option value="">Select Warehouse</option>
                                {wareHouses.map((wh) => {
                                    const whId = wh.warehouse_id ?? wh.id;
                                    return (
                                        <option key={whId} value={whId}>
                                            {wh.name}
                                        </option>
                                    );
                                })}
                            </select>
                            <div className="absolute inset-y-0 right-0 flex items-center px-2.5 pointer-events-none">
                                <FaAngleDown className="text-gray-400 dark:text-indigo-300" size={11} />
                            </div>
                        </div>
                        {wareHouses.length === 0 && (
                            <p className="text-amber-500 text-[10px] mt-0.5 font-medium">No warehouses available to select</p>
                        )}
                        {errors.warehouse_id && (
                            <p className="text-red-500 text-[10px] mt-0.5 font-medium">{errors.warehouse_id.message}</p>
                        )}
                    </>
                ) : (
                    <div className={`px-2.5 py-2 text-[13px] sm:text-sm rounded-lg border w-full flex items-center gap-1.5 truncate ${isDarkMode
                        ? "bg-indigo-500/[0.08] border-indigo-500/25 text-indigo-100"
                        : "bg-gray-50 border-gray-100 text-gray-700"
                        }`}>
                        <FaWarehouse className="text-gray-400 dark:text-indigo-300/70 flex-shrink-0" size={11} />
                        <span className="font-medium truncate">
                            {selectedWh?.name || <span className="italic text-gray-400 dark:text-indigo-300/50 font-normal">Not specified</span>}
                        </span>
                    </div>
                )}
            </div>
        );
    };

    const renderBoxDimensionsSection = () => {
        const inputCls = `w-full text-[13px] sm:text-sm rounded-lg border px-2 py-1.5 outline-none transition-all focus:ring-2 disabled:opacity-60 disabled:cursor-not-allowed ${
            isDarkMode
                ? "bg-[#1a1a2e] border-indigo-400/50 text-white placeholder-indigo-300/50 focus:border-indigo-400 focus:ring-indigo-500/40"
                : "bg-white border-gray-200 text-gray-800 placeholder-gray-400 focus:border-indigo-400 focus:ring-indigo-500/15"
        }`;
        const labelCls = `block text-[9px] mb-0.5 font-semibold uppercase tracking-wide ${isDarkMode ? "text-indigo-200/70" : "text-gray-500"}`;
        const cardCls = `rounded-lg border p-2 ${isDarkMode ? "bg-indigo-500/[0.08] border-indigo-500/25" : "bg-gray-50 border-gray-100"}`;

        if (!isEditing) {
            const items = boxSets.filter((s) => s.length || s.width || s.height || s.box_count);
            const roLabelCls = `block text-[9px] mb-0.5 font-semibold uppercase tracking-wide ${isDarkMode ? "text-indigo-200/70" : "text-gray-500"}`;
            const roValueCls = `px-2 py-1.5 text-[13px] sm:text-sm rounded-lg border w-full ${isDarkMode ? "bg-indigo-500/[0.08] border-indigo-500/25 text-indigo-100" : "bg-white border-gray-200 text-gray-800"}`;

            return (
                <div>
                    <div className={`border p-2.5 rounded-lg ${isDarkMode ? "border-indigo-500/25" : ""}`}>
                        <label className={`block text-[10px] mb-1 font-semibold uppercase tracking-wide ${isDarkMode ? "text-indigo-200/80" : "text-gray-500"}`}>
                            Box Dimensions
                        </label>
                        <div className="max-h-[150px] overflow-y-auto pr-1 custom-scrollbar space-y-1.5">
                            {items.map((s) => (
                                <div key={s.id} className={cardCls}>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                                        <div>
                                            <label className={roLabelCls}>Qty</label>
                                            <div className={roValueCls}>{s.box_count || 0}</div>
                                        </div>
                                        <div>
                                            <label className={roLabelCls}>Length</label>
                                            <div className={roValueCls}>{s.length || 0}</div>
                                        </div>
                                        <div>
                                            <label className={roLabelCls}>Width</label>
                                            <div className={roValueCls}>{s.width || 0}</div>
                                        </div>
                                        <div>
                                            <label className={roLabelCls}>Height</label>
                                            <div className={roValueCls}>{s.height || 0}</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {items.length === 0 && (
                                <div className={`px-2.5 py-2 text-[13px] sm:text-sm rounded-lg border w-full ${isDarkMode ? "bg-indigo-500/[0.08] border-indigo-500/25 text-indigo-200/60" : "bg-gray-50 border-gray-100 text-gray-700"}`}>
                                    <span className="italic text-gray-400 dark:text-indigo-300/50 font-normal">Not specified</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div>
                <div className={`border p-2.5 rounded-lg ${isDarkMode ? "border-indigo-500/25" : ""}`}>
                    <div className="flex items-center justify-between mb-1">
                        <label className={`block text-[10px] font-semibold uppercase tracking-wide ${isDarkMode ? "text-indigo-200/80" : "text-gray-500"}`}>
                            Box Dimensions
                        </label>
                        {boxTotalNum > 0 && (
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                                boxRemaining === 0
                                    ? isDarkMode ? "bg-emerald-500/15 text-emerald-300 border border-emerald-400/40" : "bg-emerald-50 text-emerald-700"
                                    : isDarkMode ? "bg-amber-500/15 text-amber-300 border border-amber-400/40" : "bg-amber-50 text-amber-700"
                            }`}>
                                {boxAssignedCount}/{boxTotalNum} assigned{boxRemaining !== 0 ? ` • ${boxRemaining} left` : ''}
                            </span>
                        )}
                    </div>

                    <div className="max-h-[150px] overflow-y-auto pr-1 custom-scrollbar space-y-1.5">
                        {boxSets.map((s) => (
                            <div key={s.id} className={cardCls}>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-1.5 items-end">
                                    <div>
                                        <label className={labelCls}>Qty</label>
                                        <input
                                            type="number"
                                            min="1"
                                            value={s.box_count}
                                            onChange={(e) => updateBoxSet(s.id, 'box_count', e.target.value)}
                                            className={inputCls}
                                            disabled={boxSets.length === 1}
                                        />
                                    </div>
                                    <div>
                                        <label className={labelCls}>Length</label>
                                        <input
                                            type="number"
                                            min="0.1"
                                            step="0.1"
                                            value={s.length}
                                            onChange={(e) => updateBoxSet(s.id, 'length', e.target.value)}
                                            className={inputCls}
                                        />
                                    </div>
                                    <div>
                                        <label className={labelCls}>Width</label>
                                        <input
                                            type="number"
                                            min="0.1"
                                            step="0.1"
                                            value={s.width}
                                            onChange={(e) => updateBoxSet(s.id, 'width', e.target.value)}
                                            className={inputCls}
                                        />
                                    </div>
                                    <div className="col-span-2 sm:col-span-1 flex items-end gap-1">
                                        <div className="flex-1">
                                            <label className={labelCls}>Height</label>
                                            <input
                                                type="number"
                                                min="0.1"
                                                step="0.1"
                                                value={s.height}
                                                onChange={(e) => updateBoxSet(s.id, 'height', e.target.value)}
                                                className={inputCls}
                                            />
                                        </div>
                                        {boxSets.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removeBoxSet(s.id)}
                                                className={`w-9 sm:w-7 h-[34px] sm:h-[30px] flex items-center justify-center rounded-lg border transition-colors ${
                                                    isDarkMode ? "border-indigo-500/40 text-red-400 hover:bg-red-500/15" : "border-gray-200 text-red-500 hover:bg-red-50"
                                                }`}
                                            >
                                                <FaTrashAlt size={10} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <button
                    type="button"
                    onClick={addBoxSet}
                    className={`mt-1.5 w-full flex items-center justify-center gap-1.5 py-2 sm:py-1.5 rounded-lg border border-dashed text-[11px] font-semibold transition-colors ${
                        isDarkMode ? "border-indigo-500/40 text-indigo-200 hover:bg-indigo-500/10" : "border-gray-300 text-gray-600 hover:bg-gray-100"
                    }`}
                >
                    <FaPlus size={9} /> Add Box Set
                </button>
            </div>
        );
    };

    const handleClose = () => {
        onClose();
    };

    if (!shipment) return null;

    return (
        <Dialog
            open={isopen}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            fullScreen={window.innerWidth < 640}
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: isDarkMode
                            ? 'rgba(0, 0, 0, 0.75)'
                            : 'rgba(15, 23, 42, 0.55)',
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                    }
                }
            }}
            PaperProps={{
                sx: {
                    borderRadius: { xs: '0px', sm: '16px' },
                    backgroundColor: isDarkMode ? '#0b0b14' : '#f9fafb',
                    backgroundImage: isDarkMode
                        ? 'linear-gradient(135deg, #0b0b14 0%, #0d0d18 50%, #0b0b14 100%)'
                        : 'none',
                    boxShadow: isDarkMode
                        ? '0 25px 70px -15px rgba(0, 0, 0, 0.9), 0 0 0 1px rgba(99, 102, 241, 0.35), 0 0 60px rgba(79, 70, 229, 0.25)'
                        : '0 20px 50px -15px rgba(0, 0, 0, 0.25)',
                    overflow: 'hidden',
                    border: isDarkMode ? '1px solid rgba(99, 102, 241, 0.35)' : 'none',
                    margin: { xs: 0, sm: 'auto' },
                    height: { xs: '100%', sm: 'auto' },
                    maxHeight: { xs: '100%', sm: '90vh' },
                }
            }}
        >
            {/* Sticky gradient header */}
            <div className="sticky top-0 z-20 bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 px-3 sm:px-4 py-3 sm:py-3.5 shadow-[0_8px_30px_rgba(79,70,229,0.35)] dark:shadow-[0_8px_40px_rgba(79,70,229,0.5)]">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <div className="w-9 h-9 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0 dark:shadow-[0_0_20px_rgba(255,255,255,0.25)]">
                            <FaWarehouse className="text-white" size={16} />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[10px] uppercase tracking-widest text-white/75 font-semibold mb-0.5">B2B Shipment</p>
                            <h2 className="text-sm md:text-base font-bold text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.25)] truncate">#{shipment?.order_id || 'Details'}</h2>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={handleClose}
                        className="w-9 h-9 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/30 backdrop-blur-sm transition-all hover:scale-110 dark:shadow-[0_0_15px_rgba(255,255,255,0.2)] flex-shrink-0"
                    >
                        <IoIosCloseCircle size={20} className="text-white" />
                    </button>
                </div>
                <div className="absolute -bottom-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent" />
            </div>

            <DialogContent
                className={`custom-scrollbar px-2.5 sm:px-3 md:px-4 pb-4 pt-3 ${isDarkMode ? "dark:bg-[#0b0b14]" : "bg-gray-50"}`}
                style={{ maxHeight: window.innerWidth < 640 ? 'calc(100vh - 60px)' : '75vh' }}
                sx={{
                    backgroundColor: isDarkMode ? '#0b0b14 !important' : undefined,
                    '&::-webkit-scrollbar': { width: '6px' },
                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar-thumb': {
                        background: isDarkMode ? 'rgba(99, 102, 241, 0.5)' : 'rgba(0,0,0,0.15)',
                        borderRadius: '3px',
                    },
                }}
            >

                {!isEditing && (
                    <div className="flex justify-end mb-2.5">
                        <button
                            type="button"
                            onClick={handleEdit}
                            disabled={shipment?.status !== 'Pending'}
                            className={`py-2 px-3 sm:px-4 rounded-lg focus:outline-none focus:ring-2 font-semibold text-[11px] sm:text-xs transition-all duration-200 flex items-center gap-1.5 shadow-sm ${
                                shipment?.status === 'Pending'
                                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-indigo-600 dark:to-purple-600 dark:shadow-lg dark:shadow-indigo-900/50 text-white hover:shadow-lg hover:shadow-blue-500/25 dark:hover:from-indigo-500 dark:hover:to-purple-500 hover:-translate-y-0.5 focus:ring-blue-500/20 dark:focus:ring-indigo-500/40'
                                    : `${isDarkMode ? 'bg-indigo-500/10 text-indigo-300/50' : 'bg-white text-gray-400'} cursor-not-allowed border border-gray-200 dark:border-indigo-500/25`
                            }`}
                        >
                            <FaEdit size={12} /> Edit Shipment
                        </button>
                    </div>
                )}

                <form className="space-y-3 font-sans" onSubmit={handleSubmit(onSubmit, onErrors)}>
                    {/* Basic Shipment Information */}
                    <div className={`rounded-xl p-2.5 sm:p-3 border transition-shadow ${
                        isDarkMode
                            ? "bg-gradient-to-br from-[#0b0b14] via-[#0d0d18] to-[#0b0b14] border-indigo-500/25 shadow-[0_4px_20px_rgba(79,70,229,0.12)] hover:shadow-[0_8px_30px_rgba(79,70,229,0.2)]"
                            : "bg-white border-gray-100 shadow-sm hover:shadow-lg"
                    }`}>
                        <div className="flex items-center gap-2.5 mb-3">
                            <SectionBadge icon={<FaBox size={12} />} gradient="from-purple-500 to-fuchsia-600" />
                            <h3 className={`text-[13px] sm:text-sm font-bold ${isDarkMode ? "text-white" : "text-gray-800"}`}>
                                Basic Shipment Information
                            </h3>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-3">
                            {renderField('Order ID', 'order_id')}
                            {renderField('LRN', 'lrn')}
                            {renderWarehouseField()}
                            {renderField('Total Boxes', 'total_boxes', 'number', true)}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-3 mt-3">
                            {renderField('Total Weight', 'total_weight', 'number', true)}
                            {renderField('ROV Insurance', 'rov_insurance', 'number', true)}
                            {renderField('Freight Mode', 'freight_mode')}
                            {renderField('Shipment Type', 'shipment_type')}
                        </div>
                        <div className="grid grid-cols-1 gap-3 mt-3">
                            {renderField('Insurance', 'insurence')}
                        </div>
                        <div className="grid grid-cols-1 gap-3 mt-3">
                            {renderBoxDimensionsSection()}
                        </div>
                    </div>

                    {/* Pickup Location Section */}
                    <div className={`rounded-xl p-2.5 sm:p-3 border transition-shadow ${
                        isDarkMode
                            ? "bg-gradient-to-br from-[#0b0b14] via-[#0d0d18] to-[#0b0b14] border-indigo-500/25 shadow-[0_4px_20px_rgba(79,70,229,0.12)] hover:shadow-[0_8px_30px_rgba(79,70,229,0.2)]"
                            : "bg-white border-gray-100 shadow-sm hover:shadow-lg"
                    }`}>
                        <div className="flex items-center gap-2.5 mb-3">
                            <SectionBadge icon={<FaTruck size={11} />} gradient="from-sky-500 to-blue-600" />
                            <h3 className={`text-[13px] sm:text-sm font-bold ${isDarkMode ? "text-white" : "text-gray-800"}`}>
                                Pickup Location
                            </h3>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-3">
                            {renderField('Consignee Name', 'pickup_consignee_name')}
                            {renderField('Phone', 'pickup_phone')}
                            {renderField('City', 'pickup_city')}
                            {renderField('ZIP Code', 'pickup_zip')}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-3 mt-3">
                            <div className="col-span-2 md:col-span-3">
                                {renderField('Address', 'pickup_address')}
                            </div>
                            {renderField('State', 'pickup_state')}
                        </div>
                    </div>

                    {/* Dropoff Location Section */}
                    <div className={`rounded-xl p-2.5 sm:p-3 border transition-shadow ${
                        isDarkMode
                            ? "bg-gradient-to-br from-[#0b0b14] via-[#0d0d18] to-[#0b0b14] border-indigo-500/25 shadow-[0_4px_20px_rgba(79,70,229,0.12)] hover:shadow-[0_8px_30px_rgba(79,70,229,0.2)]"
                            : "bg-white border-gray-100 shadow-sm hover:shadow-lg"
                    }`}>
                        <div className="flex items-center gap-2.5 mb-3">
                            <SectionBadge icon={<FaMapMarkerAlt size={11} />} gradient="from-emerald-500 to-teal-600" />
                            <h3 className={`text-[13px] sm:text-sm font-bold ${isDarkMode ? "text-white" : "text-gray-800"}`}>
                                Dropoff Location
                            </h3>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-3">
                            {renderField('Consignee Name', 'dropoff_consignee_name')}
                            {renderField('Phone', 'dropoff_phone')}
                            {renderField('City', 'dropoff_city')}
                            {renderField('ZIP Code', 'dropoff_zip')}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-3 mt-3">
                            <div className="col-span-2">
                                {renderField('Address', 'dropoff_address')}
                            </div>
                            {renderField('Email', 'dropoff_email')}
                            {renderField('State', 'dropoff_state')}
                        </div>
                    </div>

                    {/* Billing Address Section */}
                    <div className={`rounded-xl p-2.5 sm:p-3 border transition-shadow ${
                        isDarkMode
                            ? "bg-gradient-to-br from-[#0b0b14] via-[#0d0d18] to-[#0b0b14] border-indigo-500/25 shadow-[0_4px_20px_rgba(79,70,229,0.12)] hover:shadow-[0_8px_30px_rgba(79,70,229,0.2)]"
                            : "bg-white border-gray-100 shadow-sm hover:shadow-lg"
                    }`}>
                        <div className="flex items-center gap-2.5 mb-3">
                            <SectionBadge icon={<FaFileInvoice size={11} />} gradient="from-amber-500 to-orange-600" />
                            <h3 className={`text-[13px] sm:text-sm font-bold ${isDarkMode ? "text-white" : "text-gray-800"}`}>
                                Billing Address
                            </h3>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-3">
                            {renderField('Name', 'billing_name')}
                            {renderField('Company', 'billing_company')}
                            {renderField('Consignor', 'billing_consignor')}
                            {renderField('Phone', 'billing_phone')}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-3 mt-3">
                            <div className="col-span-2">
                                {renderField('Address', 'billing_address')}
                            </div>
                            {renderField('City', 'billing_city')}
                            {renderField('State', 'billing_state')}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-3 mt-3">
                            {renderField('PIN Code', 'billing_pin')}
                            {renderField('PAN Number', 'billing_pan_number')}
                            {renderField('GST Number', 'billing_gst_number')}
                        </div>
                    </div>

                    {/* Payment Details Section */}
                    <div className={`rounded-xl p-2.5 sm:p-3 border transition-shadow ${
                        isDarkMode
                            ? "bg-gradient-to-br from-[#0b0b14] via-[#0d0d18] to-[#0b0b14] border-indigo-500/25 shadow-[0_4px_20px_rgba(79,70,229,0.12)] hover:shadow-[0_8px_30px_rgba(79,70,229,0.2)]"
                            : "bg-white border-gray-100 shadow-sm hover:shadow-lg"
                    }`}>
                        <div className="flex items-center gap-2.5 mb-3">
                            <SectionBadge icon={<FaCreditCard size={11} />} gradient="from-indigo-500 to-violet-600" />
                            <h3 className={`text-[13px] sm:text-sm font-bold ${isDarkMode ? "text-white" : "text-gray-800"}`}>
                                Payment Details
                            </h3>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-3">
                            {renderField('Payment ID', 'payment_id')}
                            <div>
                                <label className={`block text-[10px] mb-1 font-semibold uppercase tracking-wide ${isDarkMode ? "text-indigo-200/80" : "text-gray-500"}`}>Payment Mode</label>
                                {isEditable('payment_mode') ? (
                                    <>
                                        <div className="relative">
                                            <select
                                                {...register('payment_mode')}
                                                className={`px-2.5 py-2 text-[13px] sm:text-sm rounded-lg border w-full outline-none appearance-none transition-all shadow-sm focus:shadow-md focus:ring-2 ${isDarkMode
                                                    ? "bg-[#1a1a2e] text-white border-indigo-400/50 focus:border-indigo-400 focus:ring-indigo-500/40"
                                                    : "bg-white border-gray-200 focus:border-indigo-400 focus:ring-indigo-500/15"
                                                    }`}
                                            >
                                                <option value="">Select Mode</option>
                                                <option value="cod">COD</option>
                                                <option value="prepaid">Prepaid</option>
                                            </select>
                                            <div className="absolute inset-y-0 right-0 flex items-center px-2.5 pointer-events-none">
                                                <FaAngleDown className="text-gray-400 dark:text-indigo-300" size={11} />
                                            </div>
                                        </div>
                                        {errors.payment_mode && (
                                            <p className="text-red-500 text-[10px] mt-0.5 font-medium">{errors.payment_mode.message}</p>
                                        )}
                                    </>
                                ) : (
                                    <div className="px-2.5 py-2 text-[13px] sm:text-sm rounded-lg border w-full bg-gradient-to-r from-indigo-500 to-violet-600 border-transparent shadow-sm dark:shadow-lg dark:shadow-indigo-900/40">
                                        <span className="font-semibold text-[11px] sm:text-xs text-white uppercase tracking-wide">{shipment?.payment_mode || 'Not specified'}</span>
                                    </div>
                                )}
                            </div>
                            {paymentMode === 'cod' && renderField('COD Amount', 'cod_amount', 'number', true)}
                        </div>
                    </div>

                    {/* Invoice Details Section */}
                    <div className={`rounded-xl p-2.5 sm:p-3 border transition-shadow ${
                        isDarkMode
                            ? "bg-gradient-to-br from-[#0b0b14] via-[#0d0d18] to-[#0b0b14] border-indigo-500/25 shadow-[0_4px_20px_rgba(79,70,229,0.12)] hover:shadow-[0_8px_30px_rgba(79,70,229,0.2)]"
                            : "bg-white border-gray-100 shadow-sm hover:shadow-lg"
                    }`}>
                        <div className="flex items-center gap-2.5 mb-3">
                            <SectionBadge icon={<FaFileInvoice size={11} />} gradient="from-orange-500 to-red-500" />
                            <h3 className={`text-[13px] sm:text-sm font-bold ${isDarkMode ? "text-white" : "text-gray-800"}`}>
                                Invoice Details
                            </h3>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3">
                            {renderField('E-way Bill', 'invoice_ewaybill')}
                            {renderField('Invoice Number', 'invoice_num')}
                            {renderField('Invoice Amount', 'invoice_amt', 'number', true)}
                        </div>
                    </div>

                    {/* Action Buttons - sticky footer style */}
                    <div className={`md:-mx-4 px-3 md:px-4 py-2.5 sm:py-2 flex flex-col sm:flex-row sm:flex-wrap sm:justify-end gap-2.5 border-t backdrop-blur-md ${
                        isDarkMode
                            ? "bg-[#0b0b14]/90 border-indigo-500/25"
                            : "bg-gray-50/90 border-gray-200"
                    }`}>
                        <button
                            type="button"
                            onClick={handleClose}
                            className={`w-full sm:w-auto py-2.5 sm:py-2 px-5 rounded-lg focus:outline-none focus:ring-2 font-semibold text-xs transition-all flex items-center justify-center gap-1.5 ${
                                isDarkMode
                                    ? "bg-indigo-500/10 text-indigo-200 border border-indigo-500/30 hover:bg-indigo-500/20 focus:ring-indigo-500/30"
                                    : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-100 focus:ring-gray-500/10"
                            }`}
                        >
                            <FaTimes size={12} /> Cancel
                        </button>
                        {isEditing && (
                            <button
                                type="submit"
                                disabled={isLoading}
                                className={`
                                    w-full sm:w-auto 
                                    bg-gradient-to-r from-purple-600 to-indigo-700 
                                    dark:shadow-lg dark:shadow-indigo-900/50
                                    text-white py-2.5 sm:py-2 px-5 rounded-lg 
                                    hover:shadow-lg hover:shadow-indigo-500/30 hover:-translate-y-0.5
                                    focus:outline-none focus:ring-2 focus:ring-purple-500/20
                                    font-semibold text-xs transition-all duration-200 flex items-center justify-center gap-1.5 shadow-sm
                                    disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed disabled:hover:translate-y-0
                                `}
                            >
                                {isLoading ? (
                                    <>
                                        <svg className="animate-spin -ml-1 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <FaSave size={12} /> Save Changes
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default B2BViewAllShipment;